import React, { useState, useEffect, useRef } from "react";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "components/ui/button";

const Assistant = () => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Reset loaded flag so the embed script can initialize
    delete window.__ao_research_agent_loaded__;

    const prefix = process.env.REACT_APP_PREFIX || "Academic_Online_Explore";
    const token = localStorage.getItem(`${prefix}_access_token`);

    if (!token) {
      setError("No access token found. Please log in to access Assistant.");
      setLoading(false);
      return;
    }

    const assistantBaseUrl =
      process.env.REACT_APP_ASSISTANT_API_BASE_URL || "http://localhost:8037";

    const embedUrl = `${assistantBaseUrl}/embed?token=${encodeURIComponent(
      token
    )}&api_base_url=${encodeURIComponent(assistantBaseUrl)}`;

    const container = containerRef.current;
    if (!container) return;

    // Clear previous children before appending new script
    container.innerHTML = "";

    const script = document.createElement("script");
    script.src = embedUrl;
    script.async = true;
    script.id = "assistant-embed-script";

    script.onload = () => {
      setLoading(false);
    };

    script.onerror = () => {
      setError("Failed to load Assistant embed script. Please check your connection or service status.");
      setLoading(false);
    };

    container.appendChild(script);

    return () => {
      delete window.__ao_research_agent_loaded__;
      if (container) {
        container.innerHTML = "";
      }
      const existingScript = document.getElementById("assistant-embed-script");
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, [retryKey]);

  return (
    <div className="relative w-full h-[calc(100vh-57px)] max-h-[calc(100vh-57px)] flex flex-col bg-white overflow-hidden">
      {/* Loading Overlay */}
      {loading && !error && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
          <span className="text-sm text-gray-500">Loading Assistant...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-3">
          <AlertCircle className="h-10 w-10 text-red-500" />
          <p className="text-sm text-red-600 max-w-md">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 mt-2"
            onClick={() => setRetryKey((prev) => prev + 1)}
          >
            <RefreshCw className="h-4 w-4" /> Try Again
          </Button>
        </div>
      )}

      {/* Style isolation & height fixes to prevent input clipping */}
      <style>{`
        #ao-research-agent {
          height: 100% !important;
          max-height: 100% !important;
          overflow: hidden !important;
        }
        #ao-research-agent .app-shell {
          width: 100% !important;
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
          overflow: hidden !important;
        }
        #ao-research-agent .chat-panel {
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
          position: relative !important;
          overflow: hidden !important;
        }
        #ao-research-agent .sidebar {
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }
        #ao-research-agent .result-panel {
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }
        #ao-research-agent .composer-wrap {
          bottom: 0 !important;
          padding-bottom: 24px !important;
        }
        @media (min-width: 769px) {
          #ao-research-agent .sidebar {
            position: static !important;
            top: auto !important;
            left: auto !important;
            width: auto !important;
            height: 100% !important;
          }
        }
      `}</style>

      {/* Embed Container Div - matches container expected by embed script */}
      <div
        id="ao-research-agent"
        ref={containerRef}
        className={`w-full h-full flex-1 min-h-0 ${error ? "hidden" : "flex flex-col"}`}
        style={{ width: "100%", height: "100%", maxHeight: "100%", overflow: "hidden" }}
      />
    </div>
  );
};

export default Assistant;

